import {
  NavigationGuardProvider,
  useNavigationGuardContext,
} from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import {
  FormValues,
  ManageTaskTimeLogForm,
  ManageTaskTimeLogFormHandle,
} from "@jugl-web/domain-resources/task-time-tracking/components/ManageTaskTimeLogForm";
import { UserRole } from "@jugl-web/rest-api";
import { TaskTimeLogModel } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/TaskTimeLogModel";
import { Button, Dialog, InteractiveContainer } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import { useFormSubmittingState } from "@jugl-web/utils/form/hooks/useFormSubmittingState";
import { FC, useRef } from "react";
import { ReactComponent as CrossIcon } from "./assets/cross.svg";

interface ManageTaskTimeLogDialogProps {
  entityId: string;
  meId: string;
  boardId: string | null;
  userRole: UserRole | undefined;
  isOpen: boolean;
  isTaskSelectable: boolean;
  editingTimeLog?: TaskTimeLogModel;
  initialFormValues?: Partial<FormValues>;
  onClose: () => void;
  onAfterSubmit?: (timeLog: TaskTimeLogModel) => void;
  onRequestCreateTask?: (formValues?: FormValues) => void;
}

export const ManageTaskTimeLogDialogInner: FC<ManageTaskTimeLogDialogProps> = ({
  entityId,
  meId,
  boardId,
  userRole,
  isOpen,
  isTaskSelectable,
  editingTimeLog,
  initialFormValues,
  onClose,
  onAfterSubmit,
  onRequestCreateTask,
}) => {
  const { isSubmitting, withSubmitting } = useFormSubmittingState();

  const { requestNavigation } = useNavigationGuardContext();

  const { t } = useTranslations();
  const formRef = useRef<ManageTaskTimeLogFormHandle>(null);

  const isEditing = !!editingTimeLog;

  const headerTitle = isEditing
    ? t({
        id: "tasks-page.edit-time-log",
        defaultMessage: "Edit Log",
      })
    : t({
        id: "tasks-page.add-time-log",
        defaultMessage: "Add Log",
      });

  const handleSubmit = () => {
    withSubmitting(formRef.current?.submit);
  };

  const handleAfterSubmit = (timeLog: TaskTimeLogModel) => {
    onAfterSubmit?.(timeLog);
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => requestNavigation(onClose)}
      className={cx("flex max-h-[90vh] w-[620px] flex-col")}
    >
      <div
        className={cx(
          "flex min-h-[40px] items-center justify-between border-x-0 border-t-0 border-b border-solid border-b-gray-100 px-6 py-2"
        )}
      >
        <span className="font-secondary text-sm font-medium text-dark-800">
          {headerTitle}
        </span>
        <InteractiveContainer
          className="ml-auto h-8 w-8"
          onClick={() => requestNavigation(onClose)}
        >
          <CrossIcon />
        </InteractiveContainer>
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto px-4 pt-6 pb-4">
        <ManageTaskTimeLogForm
          ref={formRef}
          entityId={entityId}
          meId={meId}
          boardId={boardId}
          userRole={userRole}
          editingTimeLog={editingTimeLog}
          isTaskSelectable={isTaskSelectable}
          initialFormValues={initialFormValues}
          onAfterSubmit={handleAfterSubmit}
          onRequestCreateTask={onRequestCreateTask}
        />
        <div className="mt-10 flex items-center gap-3 [&>button]:flex-1">
          <Button
            variant="tertiary"
            className="h-10"
            onClick={() => requestNavigation(onClose)}
          >
            {t({ id: "common.cancel", defaultMessage: "Cancel" })}
          </Button>
          <Button
            className="h-10"
            isDisabled={isSubmitting}
            onClick={handleSubmit}
          >
            {isEditing
              ? t({ id: "common.save", defaultMessage: "Save" })
              : t({ id: "common.add", defaultMessage: "Add" })}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export const ManageTaskTimeLogDialog: FC<ManageTaskTimeLogDialogProps> = (
  props
) => {
  return (
    <NavigationGuardProvider>
      <ManageTaskTimeLogDialogInner {...props} />
    </NavigationGuardProvider>
  );
};
